/**
 * InDeep-Net
 * Copyright (C) 2025
 * Authors: Fabien MAREUIL
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */
import axios from "axios";
import { TrajectoryFromModelAndCoordinates } from 'molstar/lib/mol-plugin-state/transforms/model';
import { MolScriptBuilder as MS } from 'molstar/lib/mol-script/language/builder';
import { createStructureRepresentationParams } from 'molstar/lib/mol-plugin-state/helpers/structure-representation-params';
import { getColorListFromName } from 'molstar/lib/mol-util/color/lists';
import { StateTransforms } from 'molstar/lib/mol-plugin-state/transforms';
import * as tus from "tus-js-client";
import { WarningMessage, startbarUpload, progressbarUpload, finishbarUpload } from '../Utils.js';
import { updateIsLoaded, updateIsActivated } from './HookStateUpdate.jsx';


async function loadStructuretoMolstarforTraj(plugin, urlfile, formatStructure) {
    const isBinary = false;
    const filenamestr = urlfile.substring(urlfile.lastIndexOf('/') + 1);
    const data = await plugin.builders.data.download({ url: urlfile, isBinary: isBinary, label: filenamestr }, { state: { isGhost: true } });
    const trajectory = await plugin.builders.structure.parseTrajectory(data, formatStructure);
    const model = await plugin.builders.structure.createModel(trajectory);
    updateIsLoaded(true);
    updateIsActivated(true);
    return { 'model': model, 'filenamestr': filenamestr }
}

async function loadTrajtoMolstarforTraj(plugin, urltraj, formatTrajectory, model) {
    const isBinary = true;
    const filenametraj = urltraj.substring(urltraj.lastIndexOf('/') + 1);
    try {
        const datacoords = await plugin.builders.data.download({ url: urltraj, isBinary: isBinary, label: filenametraj }, { state: { isGhost: true } });
        const providercoord = await plugin.dataFormats.get(formatTrajectory);
        const coords = await providercoord.parse(plugin, datacoords);
        const newtrajectory = await plugin.build().toRoot()
            .apply(TrajectoryFromModelAndCoordinates, {
                modelRef: model.ref,
                coordinatesRef: coords.ref
            }, { dependsOn: [model.ref, coords.ref] })
            .commit();
        const preset = await plugin.builders.structure.hierarchy.applyPreset(newtrajectory, 'default');

        const sel = MS.struct.generator.atomGroups({
            'atom-test': MS.core.rel.eq([MS.struct.atomProperty.macromolecular.label_comp_id(), 'PSD']),
        });
        const structure = preset.structure;
        const components = {
            polymer: await plugin.builders.structure.tryCreateComponentStatic(structure, 'polymer'),
            ligand: await plugin.builders.structure.tryCreateComponentStatic(structure, 'ligand'),
            water: await plugin.builders.structure.tryCreateComponentStatic(structure, 'water'),
        };
        const update = plugin.build();
        const builder = plugin.builders.structure.representation;
        if (components.polymer) builder.buildRepresentation(update, components.polymer, { type: 'cartoon', color: 'uncertainty' }, { tag: 'polymer' });
        if (components.ligand) builder.buildRepresentation(update, components.ligand, { type: 'ball-and-stick' }, { tag: 'ligand' });
        if (components.water) builder.buildRepresentation(update, components.water, { type: 'ball-and-stick' }, { tag: 'water' });
        var dcdcolor = getColorListFromName('red-yellow-green');
        update.to(structure)
            .apply(StateTransforms.Model.StructureSelectionFromExpression, { label: 'Pockets', expression: sel })
            .apply(StateTransforms.Representation.StructureRepresentation3D, createStructureRepresentationParams(plugin, structure.data, {
                type: 'ball-and-stick',
                //type: 'gaussian-volume'',
                //typeParams: { alpha: 0.5 },
                typeParams: { alpha: 1 },
                //color: 'element-index',
                color: 'uncertainty',
                //color: 'element-index',
                colorParams: { domain: [0, 150], list: { kind: 'set', colors: dcdcolor.list } },
                size: 'physical',
                sizeParams: { scale: 3 }
            }));
        await update.commit();
    } catch (error) {
        WarningMessage("An Error has occurred while loading the trajectory, check if the format is correct.");
    }
    return { 'filenametraj': filenametraj }
}

async function loadTrajectorytoMolstar(plugin, data_file, data_id, formatStructure, formDataTrajectory, formatTrajectory) {
    const structloader = await loadStructuretoMolstarforTraj(plugin, data_file, formatStructure);
    const filenamestr = structloader['filenamestr'];
    const model = structloader['model'];
    formDataTrajectory.append("structure", data_id);
    formDataTrajectory.append("format", formatTrajectory)
    try {
        axios({
            method: "post",
            url: "/api/trajectory/",
            data: formDataTrajectory,
            headers: { "Content-Type": "multipart/form-data" },
        }).then(
            async function (responsetraj) {
                const trajloader = await loadTrajtoMolstarforTraj(plugin, responsetraj.data.file, responsetraj.data.format, model)
                const filenametraj = trajloader['filenametraj']
                var structurestorage = JSON.parse(sessionStorage.getItem('structures'));
                if (!structurestorage.hasOwnProperty(filenamestr)) {
                    structurestorage[filenamestr] = { 'id': data_id, 'trajectories': {} };
                }
                structurestorage[filenamestr]['trajectories'][filenametraj] = { 'id': responsetraj.data.id };
                sessionStorage.setItem('structures', JSON.stringify(structurestorage));
                let volumediv = document.getElementById('volumediv');
                volumediv.classList.remove('hidden');
            }

        );
    } catch (error) {
        WarningMessage(error)
    }
}

function upload_tusd(plugin, formDataTrajectory, type, structure_file, structure_id, structure_format, tusid) {
    var file = formDataTrajectory.get("file");
    var mimetype = 'chemical/x-pdb';
    // only use for dcd upload
    if (type === 'dcd' || type === 'xtc') {
        mimetype = 'application/octet-stream';
    }
    var upload = new tus.Upload(file, {
        // Endpoint is the upload creation URL from your tus server
        endpoint: "/files/",
        // Retry delays will enable tus-js-client to automatically retry on errors
        retryDelays: [0, 3000, 5000, 10000, 20000],
        // Attach additional meta data about the file for the server
        //chunkSize: 256000000, // mandatory if we fix a body size limit in the nginx conf
        metadata: {
            //removeFingerprintOnSuccess: true,
            filename: file.name,
            filetype: mimetype
        },
        // Callback for errors which cannot be fixed using retries
        onError: function (error) {
            WarningMessage("Failed because: " + error)
        },
        // Callback for reporting upload progress
        onProgress: function (bytesUploaded, bytesTotal) {
            var percentage = (bytesUploaded / bytesTotal * 100).toFixed(2)
            progressbarUpload(tusid, percentage)
        },
        // Callback for once the upload is completed
        onSuccess: function () {
            console.log("Download %s from %s%s", upload.file.name, window.location.protocol, upload.url)
            // TODO: Why window.location.protocol is now mandatory?
            formDataTrajectory.delete('file');
            formDataTrajectory.append('tusfile', window.location.protocol + upload.url)
            formDataTrajectory.append('tusnamefile', upload.file.name)
            loadTrajectorytoMolstar(plugin, structure_file, structure_id, structure_format, formDataTrajectory, type)
            finishbarUpload(tusid)
        }
    })
    startbarUpload(tusid)
    upload.start()
}

export const AxiosLoadTrajectorytoMolstar = async (plugin, formDataStructure, formDataTrajectory, formatStructure, formatTrajectory, tusid) => {
    try {
        await axios({
            method: "post",
            url: "/api/structure/",
            data: formDataStructure,
            headers: { "Content-Type": "multipart/form-data" },
        }).then(
            function (responsestructure) {
                upload_tusd(plugin, formDataTrajectory, formatTrajectory, responsestructure.data.file, responsestructure.data.id, formatStructure, tusid);
            }
        );
    } catch (error) {
        WarningMessage(error)
    }
}

export const LoadResultTrajectorytoMolstar = async (plugin, urlstructure, formatStructure, structure_id, urltrajectory, formatTrajectory, trajectory_id) => {
    const structloader = await loadStructuretoMolstarforTraj(plugin, urlstructure, formatStructure);
    const filenamestr = structloader['filenamestr'];
    const model = structloader['model'];
    const trajloader = await loadTrajtoMolstarforTraj(plugin, urltrajectory, formatTrajectory, model);
    const filenametraj = trajloader['filenametraj'];
    var structurestorage = JSON.parse(sessionStorage.getItem('structures'));
    if (!structurestorage.hasOwnProperty(filenamestr)) {
        structurestorage[filenamestr] = { 'id': structure_id, 'trajectories': {} };
    }
    structurestorage[filenamestr]['trajectories'][filenametraj] = { 'id': trajectory_id };
    sessionStorage.setItem('structures', JSON.stringify(structurestorage));
    let volumediv = document.getElementById('volumediv');
    volumediv.classList.remove('hidden');
}
